import React, { useEffect, useMemo, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { auth, database } from '../firebase';

type PromptTestResultKind = 'json' | 'image' | 'unknown';
type ImageScale = '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '4:5' | '5:4' | '9:16' | '16:9' | '21:9';
type ImageResolution = '512' | '1K' | '2K' | '4K';
type ThinkingLevel = 'minimal' | 'low' | 'medium' | 'high';
type ThinkingLevelOption = 'default' | ThinkingLevel;

type PromptPricing = {
  model?: string;
  tokens?: Record<string, number>;
  costs?: Record<string, number>;
};

type PromptTestResponse = {
  results: string;
  pricing?: PromptPricing;
};

type PricingItem = {
  model_id?: string;
};

const promptTesterEndpoint = 'https://prompt-test-slyn3k2c5q-uc.a.run.app';
const imageScaleOptions: ImageScale[] = ['1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'];
const imageResolutionOptions: ImageResolution[] = ['512', '1K', '2K', '4K'];
const thinkingLevelOptions: ThinkingLevel[] = ['minimal', 'low', 'medium', 'high'];

const bytesFromBase64 = (base64Value: string) => {
  const cleanBase64 = base64Value.replace(/\s/g, '');
  const binary = window.atob(cleanBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const detectMimeType = (base64Value: string) => {
  if (base64Value.startsWith('iVBORw0KGgo')) {
    return 'image/png';
  }
  if (base64Value.startsWith('/9j/')) {
    return 'image/jpeg';
  }
  if (base64Value.startsWith('R0lGOD')) {
    return 'image/gif';
  }
  if (base64Value.startsWith('UklGR')) {
    return 'image/webp';
  }
  return 'application/octet-stream';
};

const decodeBase64Json = (base64Value: string) => {
  try {
    const bytes = bytesFromBase64(base64Value);
    const text = new TextDecoder().decode(bytes);
    return JSON.parse(text) as unknown;
  } catch (error) {
    return null;
  }
};

const buildSchemaFromFields = (fields: string[]) => {
  const properties = Object.fromEntries(fields.map((field) => [field, { type: 'string' }]));
  return JSON.stringify({
    type: 'object',
    properties,
    required: fields,
  });
};

const extractPromptVariables = (template: string) => {
  const regex = /\{([A-Za-z0-9_]+)\}/g;
  const unique = new Set<string>();
  let match = regex.exec(template);
  while (match) {
    const key = match[1]?.trim();
    if (key) {
      unique.add(key);
    }
    match = regex.exec(template);
  }
  return Array.from(unique);
};

const toArrayBuffer = (bytes: Uint8Array) => {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
};

function PromptTestSection() {
  const [promptTemplate, setPromptTemplate] = useState('');
  const [schemaFieldInput, setSchemaFieldInput] = useState('');
  const [schemaFields, setSchemaFields] = useState<string[]>([]);
  const [promptModelId, setPromptModelId] = useState('');
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [modelsStatus, setModelsStatus] = useState('Cargando modelos...');
  const [promptTestStatus, setPromptTestStatus] = useState('');
  const [promptTestStatusType, setPromptTestStatusType] = useState<
    'neutral' | 'success' | 'error'
  >('neutral');
  const [isTestingPrompt, setIsTestingPrompt] = useState(false);
  const [promptTestResultKind, setPromptTestResultKind] =
    useState<PromptTestResultKind>('unknown');
  const [promptTestJsonResult, setPromptTestJsonResult] = useState<string>('');
  const [promptTestImageUrl, setPromptTestImageUrl] = useState('');
  const [promptTestPricing, setPromptTestPricing] = useState<PromptPricing | null>(null);
  const [promptTestDownload, setPromptTestDownload] = useState<{
    bytes: Uint8Array;
    mimeType: string;
    fileName: string;
  } | null>(null);
  const [imageScale, setImageScale] = useState<ImageScale>('1:1');
  const [imageResolution, setImageResolution] = useState<ImageResolution>('1K');
  const [thinkingLevel, setThinkingLevel] = useState<ThinkingLevelOption>('default');
  const [enableWebSearch, setEnableWebSearch] = useState(false);
  const [inputImages, setInputImages] = useState<string[]>([]);
  const [inputImageNames, setInputImageNames] = useState<string[]>([]);
  const [promptInputValues, setPromptInputValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const pricingRef = ref(database, 'Pricing');
    setModelsStatus('Cargando modelos...');
    const unsubscribe = onValue(
      pricingRef,
      (snapshot) => {
        const value = snapshot.val() as Record<string, PricingItem> | null;
        if (!value) {
          setAvailableModels([]);
          setPromptModelId('');
          setModelsStatus('No hay modelos configurados.');
          return;
        }

        const models = Object.entries(value)
          .map(([key, item]) => item.model_id || key)
          .filter((model) => model.trim().length > 0);
        const uniqueModels = Array.from(new Set(models));
        setAvailableModels(uniqueModels);
        setPromptModelId((prev) => {
          if (prev && uniqueModels.includes(prev)) {
            return prev;
          }
          return uniqueModels[0] ?? '';
        });
        setModelsStatus('');
      },
      () => {
        setAvailableModels([]);
        setPromptModelId('');
        setModelsStatus('Error al cargar modelos.');
      }
    );

    return () => unsubscribe();
  }, []);

  const isImageModel = useMemo(
    () => promptModelId.toLowerCase().includes('image'),
    [promptModelId]
  );
  const promptVariables = useMemo(
    () => extractPromptVariables(promptTemplate),
    [promptTemplate]
  );
  const promptWithVariablesApplied = useMemo(
    () =>
      promptTemplate.replace(/\{([A-Za-z0-9_]+)\}/g, (fullMatch, rawKey) => {
        const key = String(rawKey ?? '').trim();
        if (!key) {
          return fullMatch;
        }
        const value = promptInputValues[key];
        if (value == null || value.length === 0) {
          return fullMatch;
        }
        return value;
      }),
    [promptTemplate, promptInputValues]
  );

  useEffect(() => {
    setPromptInputValues((prev) => {
      const next = Object.fromEntries(
        promptVariables.map((key) => [key, prev[key] ?? ''])
      ) as Record<string, string>;
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [promptVariables]);

  const clearPromptResult = () => {
    if (promptTestImageUrl) {
      window.URL.revokeObjectURL(promptTestImageUrl);
    }
    setPromptTestResultKind('unknown');
    setPromptTestJsonResult('');
    setPromptTestImageUrl('');
    setPromptTestPricing(null);
    setPromptTestDownload(null);
  };

  const resetPromptTester = () => {
    clearPromptResult();
    setPromptTemplate('');
    setPromptInputValues({});
    setSchemaFieldInput('');
    setSchemaFields([]);
    setThinkingLevel('default');
    setEnableWebSearch(false);
    setInputImages([]);
    setInputImageNames([]);
    setPromptTestStatus('');
    setPromptTestStatusType('neutral');
  };

  const handleSchemaAdd = () => {
    const trimmed = schemaFieldInput.trim();
    if (!trimmed) {
      return;
    }
    if (schemaFields.includes(trimmed)) {
      setPromptTestStatus('Ese campo ya existe en el schema.');
      setPromptTestStatusType('error');
      return;
    }
    setSchemaFields((prev) => [...prev, trimmed]);
    setSchemaFieldInput('');
    setPromptTestStatus('');
  };

  const handleSchemaRemove = (field: string) => {
    setSchemaFields((prev) => prev.filter((item) => item !== field));
  };

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
      reader.readAsDataURL(file);
    });

  const handleInputImagesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) {
      return;
    }

    try {
      const base64Images = await Promise.all(
        files.map(async (file) => {
          const dataUrl = await readFileAsDataUrl(file);
          return dataUrl.includes(',') ? dataUrl.split(',')[1] || '' : dataUrl;
        })
      );

      const validImages = base64Images.filter((image) => image.trim().length > 0);
      setInputImages((prev) => [...prev, ...validImages]);
      setInputImageNames((prev) => [...prev, ...files.map((file) => file.name)]);
      setPromptTestStatus('');
      setPromptTestStatusType('neutral');
      event.target.value = '';
    } catch (error) {
      setPromptTestStatus('Error al leer las imágenes seleccionadas.');
      setPromptTestStatusType('error');
    }
  };

  const handleRemoveInputImage = (indexToRemove: number) => {
    setInputImages((prev) => prev.filter((_, index) => index !== indexToRemove));
    setInputImageNames((prev) => prev.filter((_, index) => index !== indexToRemove));
  };
  const handlePromptInputValueChange = (key: string, value: string) => {
    setPromptInputValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePromptTest = async () => {
    const user = auth.currentUser;
    if (!user) {
      setPromptTestStatus('Inicia sesión para probar el prompt.');
      setPromptTestStatusType('error');
      return;
    }

    clearPromptResult();
    setIsTestingPrompt(true);
    setPromptTestStatus('Probando prompt...');
    setPromptTestStatusType('neutral');

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(() => {
      abortController.abort();
    }, 5 * 60 * 1000);

    try {
      const trimmedPrompt = promptWithVariablesApplied.trim();
      if (!trimmedPrompt) {
        setPromptTestStatus('Escribe un prompt para continuar.');
        setPromptTestStatusType('error');
        return;
      }

      const token = await user.getIdToken();
      const payload: {
        prompt: string;
        model_id: string;
        schema?: string;
        image_scale?: ImageScale;
        image_resolution?: ImageResolution;
        thinking_level?: ThinkingLevel;
        enable_web_search?: boolean;
        input_images?: string[];
      } = {
        prompt: trimmedPrompt,
        model_id: promptModelId.trim(),
      };

      if (inputImages.length > 0) {
        payload.input_images = inputImages;
      }

      if (isImageModel) {
        payload.image_scale = imageScale;
        payload.image_resolution = imageResolution;
      } else {
        payload.enable_web_search = enableWebSearch;
        if (schemaFields.length > 0) {
          payload.schema = buildSchemaFromFields(schemaFields);
        }
      }
      if (thinkingLevel !== 'default') {
        payload.thinking_level = thinkingLevel;
      }

      const response = await fetch(promptTesterEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        signal: abortController.signal,
        body: JSON.stringify(payload),
      });

      window.clearTimeout(timeoutId);

      if (!response.ok) {
        let backendMessage = '';
        try {
          backendMessage = (await response.text()).trim();
        } catch (readError) {
          backendMessage = '';
        }
        setPromptTestStatus(
          backendMessage ? `Error: ${backendMessage}` : 'Error al probar el prompt.'
        );
        setPromptTestStatusType('error');
        return;
      }

      const data = (await response.json()) as PromptTestResponse;
      if (!data.results) {
        setPromptTestStatus('La respuesta no incluyó resultados.');
        setPromptTestStatusType('error');
        return;
      }

      setPromptTestPricing(data.pricing ?? null);
      const decodedJson = decodeBase64Json(data.results);
      if (decodedJson) {
        const jsonString = JSON.stringify(decodedJson, null, 2);
        setPromptTestJsonResult(jsonString);
        setPromptTestResultKind('json');
        setPromptTestDownload({
          bytes: bytesFromBase64(data.results),
          mimeType: 'application/json',
          fileName: 'prompt-result.json',
        });
      } else {
        const mimeType = detectMimeType(data.results);
        const bytes = bytesFromBase64(data.results);
        const blob = new Blob([toArrayBuffer(bytes)], { type: mimeType });
        const imageUrl = window.URL.createObjectURL(blob);
        setPromptTestImageUrl(imageUrl);
        setPromptTestResultKind('image');
        setPromptTestDownload({
          bytes,
          mimeType,
          fileName: `prompt-result.${mimeType.split('/')[1] || 'bin'}`,
        });
      }

      setPromptTestStatus('Prompt probado con éxito.');
      setPromptTestStatusType('success');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        setPromptTestStatus('La solicitud superó el tiempo de espera de 5 minutos.');
      } else {
        setPromptTestStatus('Error al probar el prompt.');
      }
      setPromptTestStatusType('error');
    } finally {
      window.clearTimeout(timeoutId);
      setIsTestingPrompt(false);
    }
  };

  const handleDownloadPromptResult = () => {
    if (!promptTestDownload) {
      return;
    }
    const blob = new Blob([toArrayBuffer(promptTestDownload.bytes)], {
      type: promptTestDownload.mimeType,
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = promptTestDownload.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(downloadUrl);
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Probar prompt</h2>
        <button
          className="secondary-button"
          type="button"
          onClick={resetPromptTester}
          disabled={isTestingPrompt}
        >
          Reiniciar
        </button>
      </div>

      <div className="prompt-ready">
        <label className="field">
          <span>Prompt</span>
          <textarea
            className="table-input prompt-tester-textarea"
            value={promptTemplate}
            onChange={(event) => setPromptTemplate(event.target.value)}
            placeholder="Escribe tu prompt aquí"
          />
        </label>
        {promptVariables.length ? (
          <div className="table table-compact">
            <div className="table-row table-header prompt-tester-schema-row">
              <div>Input</div>
              <div>Valor</div>
            </div>
            {promptVariables.map((key) => (
              <div className="table-row prompt-tester-schema-row" key={key}>
                <div className="mono">{key}</div>
                <div>
                  <input
                    className="table-input"
                    type="text"
                    value={promptInputValues[key] ?? ''}
                    onChange={(event) => handlePromptInputValueChange(key, event.target.value)}
                    placeholder={`Valor para ${key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="hint">Sin variables detectadas en el prompt.</div>
        )}

        <div className="field">
          <span>Prompt final</span>
          <pre className="prompt-result-json">{promptWithVariablesApplied || '-'}</pre>
        </div>

          <label className="field">
            <span>Modelo de IA</span>
            <select
              className="table-input"
              value={promptModelId}
              onChange={(event) => setPromptModelId(event.target.value)}
              disabled={!availableModels.length}
            >
              {availableModels.length ? null : (
                <option value="">{modelsStatus || 'Sin modelos'}</option>
              )}
              {availableModels.map((modelId) => (
                <option key={modelId} value={modelId}>
                  {modelId}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Thinking level</span>
            <select
              className="table-input"
              value={thinkingLevel}
              onChange={(event) => setThinkingLevel(event.target.value as ThinkingLevelOption)}
            >
              <option value="default">Default</option>
              {thinkingLevelOptions.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </label>
          {!isImageModel ? (
            <label className="field">
              <span>Enable web search</span>
              <input
                type="checkbox"
                checked={enableWebSearch}
                onChange={(event) => setEnableWebSearch(event.target.checked)}
              />
            </label>
          ) : null}
          {modelsStatus ? <div className="hint">{modelsStatus}</div> : null}

          {isImageModel ? (
            <>
              <label className="field">
                <span>Escala</span>
                <select
                  className="table-input"
                  value={imageScale}
                  onChange={(event) => setImageScale(event.target.value as ImageScale)}
                >
                  {imageScaleOptions.map((scale) => (
                    <option key={scale} value={scale}>
                      {scale}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Resolución</span>
                <select
                  className="table-input"
                  value={imageResolution}
                  onChange={(event) => setImageResolution(event.target.value as ImageResolution)}
                >
                  {imageResolutionOptions.map((resolution) => (
                    <option key={resolution} value={resolution}>
                      {resolution}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            <div className="field">
              <span>Outputs</span>
              <div className="project-search">
                <input
                  type="text"
                  value={schemaFieldInput}
                  onChange={(event) => setSchemaFieldInput(event.target.value)}
                  placeholder="Agregar campo"
                />
                <button className="secondary-button" type="button" onClick={handleSchemaAdd}>
                  Agregar
                </button>
              </div>
            </div>
          )}

          {!isImageModel && schemaFields.length ? (
            <div className="table table-compact">
              <div className="table-row table-header prompt-tester-schema-row">
                <div>Campo</div>
                <div>Acción</div>
              </div>
              {schemaFields.map((field) => (
                <div className="table-row prompt-tester-schema-row" key={field}>
                  <div className="mono">{field}</div>
                  <div className="table-action">
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => handleSchemaRemove(field)}
                    >
                      Quitar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <label className="field">
            <span>Input imgs</span>
            <input
              className="table-input"
              type="file"
              accept=".png,image/png"
              multiple
              onChange={handleInputImagesChange}
            />
          </label>
          {inputImageNames.length ? (
            <div className="table table-compact">
              <div className="table-row table-header prompt-tester-files-row">
                <div>Archivo</div>
                <div>Tipo</div>
                <div>Acción</div>
              </div>
              {inputImageNames.map((fileName, index) => (
                <div className="table-row prompt-tester-files-row" key={`${fileName}-${index}`}>
                  <div className="mono">{fileName}</div>
                  <div>.png</div>
                  <div className="table-action">
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => handleRemoveInputImage(index)}
                    >
                      Quitar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="panel-actions-inline">
            <button
              className="primary-button"
              type="button"
              onClick={handlePromptTest}
              disabled={isTestingPrompt}
            >
              {isTestingPrompt ? 'Probando...' : 'Probar prompt'}
            </button>
            {promptTestDownload ? (
              <button className="secondary-button" type="button" onClick={handleDownloadPromptResult}>
                Descargar archivo
              </button>
            ) : null}
          </div>
          {promptTestStatus ? (
            <div className={`inline-toast inline-toast-${promptTestStatusType}`}>{promptTestStatus}</div>
          ) : null}
      </div>

      {promptTestResultKind === 'image' && promptTestImageUrl ? (
        <div className="prompt-result-card">
          <div className="message-label">Imagen generada</div>
          <img className="prompt-result-image" src={promptTestImageUrl} alt="Prompt result" />
        </div>
      ) : null}

      {promptTestResultKind === 'json' && promptTestJsonResult ? (
        <div className="prompt-result-card">
          <div className="message-label">JSON generado</div>
          <pre className="prompt-result-json">{promptTestJsonResult}</pre>
        </div>
      ) : null}

      {promptTestPricing ? (
        <div className="prompt-result-card">
          <div className="message-label">Pricing</div>
          <div className="prompt-pricing-grid">
            <div>
              <strong>Modelo</strong>
              <div className="mono">{promptTestPricing.model || '-'}</div>
            </div>
            <div>
              <strong>Tokens</strong>
              <pre className="prompt-result-json">
                {JSON.stringify(promptTestPricing.tokens ?? {}, null, 2)}
              </pre>
            </div>
            <div>
              <strong>Costos</strong>
              <pre className="prompt-result-json">
                {JSON.stringify(promptTestPricing.costs ?? {}, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PromptTestSection;
